/**
 * expectedErrors.ts
 *
 * Recognises backend errors that are part of normal use (an expired session,
 * a message someone already deleted, invalid input) so they are not reported
 * to Sentry. The user still sees the snackbar; only the report is dropped.
 */
import { Code, ConnectError } from "@connectrpc/connect";
import type { Event, EventHint } from "@sentry/vue";
import { BackendError } from "@/services/interceptors";

/**
 * Connect codes that describe the request or the user's state rather than a
 * fault. Internal, Unknown, Unavailable, DeadlineExceeded and the rest are
 * still reported.
 */
export const EXPECTED_ERROR_CODES: ReadonlySet<Code> = new Set([
  Code.Unauthenticated,
  Code.NotFound,
  Code.InvalidArgument,
  Code.PermissionDenied,
  Code.AlreadyExists,
  Code.FailedPrecondition,
]);

/** How deep to follow `cause` links; the real chain is at most two long. */
const MAX_CAUSE_DEPTH = 5;

/**
 * Finds the Connect code an error stands for.
 *
 * Follows the `cause` chain, because the Connect transport re-wraps a
 * {@link BackendError} thrown by the error-transform interceptor as a
 * `ConnectError` with `Code.Unknown`. A ConnectError's own code wins unless
 * it is that `Unknown` wrapper.
 *
 * @param error - Anything thrown.
 * @returns The meaningful code, or undefined when there is none.
 */
export function connectCodeOf(error: unknown): Code | undefined {
  let current: unknown = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH && current; depth++) {
    if (current instanceof BackendError) return current.code;
    if (current instanceof ConnectError && current.code !== Code.Unknown) return current.code;
    current = current instanceof Error ? current.cause : undefined;
  }
  return undefined;
}

/**
 * Reports whether an error is an expected business error.
 *
 * @param error - Anything thrown.
 * @returns True when its Connect code is in {@link EXPECTED_ERROR_CODES}.
 */
export function isExpectedBusinessError(error: unknown): boolean {
  const code = connectCodeOf(error);
  return code !== undefined && EXPECTED_ERROR_CODES.has(code);
}

/**
 * Sentry event processor that drops expected business errors.
 *
 * Runs for every captured event, including unhandled rejections from callers
 * that rethrow, so the filter does not depend on each capture site.
 *
 * @param event - The event about to be sent.
 * @param hint - Carries the original thrown value.
 * @returns The event, or null to drop it.
 */
export function dropExpectedBusinessErrors(event: Event, hint: EventHint): Event | null {
  return isExpectedBusinessError(hint.originalException) ? null : event;
}
